import { useEffect, useMemo, useRef, useState } from "react";
import { AVAILABLE_MENU_LANGS } from "../../i18n/languages.js";

const LANGS = AVAILABLE_MENU_LANGS.map((lang) => ({
  value: lang.code,
  label: lang.short,
  fullLabel: lang.label,
  flag: lang.flag,
  emoji: lang.emoji,
}));

function normalizeLangList(value, fallback = LANGS.map((x) => x.value)) {
  const allowed = new Set(LANGS.map((x) => x.value));

  let arr = [];

  if (Array.isArray(value)) {
    arr = value;
  } else if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      arr = Array.isArray(parsed) ? parsed : [];
    } catch {
      arr = [];
    }
  }

  const cleaned = [
    ...new Set(
      arr
        .map((x) => String(x || "").trim().toLowerCase())
        .filter((x) => allowed.has(x))
    ),
  ];

  return cleaned.length ? cleaned : fallback;
}

function FlagIcon({ lang, style }) {
  if (lang.flag) {
    return <img src={lang.flag} alt={lang.label} style={style} />;
  }

  return (
    <span
      aria-hidden="true"
      style={{
        ...style,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 14,
        lineHeight: 1,
        background: "rgba(255,255,255,0.08)",
      }}
    >
      {lang.emoji || lang.label}
    </span>
  );
}

export default function LangSelect({ value, onChange, availableLangs }) {
  const visibleLangCodes = useMemo(
    () => normalizeLangList(availableLangs),
    [availableLangs]
  );

  const visibleLangs = useMemo(
    () => LANGS.filter((lang) => visibleLangCodes.includes(lang.value)),
    [visibleLangCodes]
  );

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(
    Math.max(0, visibleLangs.findIndex((l) => l.value === value))
  );

  const rootRef = useRef(null);
  const btnRef = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(
    () =>
      visibleLangs.find((l) => l.value === value) ||
      LANGS.find((l) => l.value === value) ||
      visibleLangs[0] ||
      LANGS[0],
    [value, visibleLangs]
  );

  useEffect(() => {
    const idx = visibleLangs.findIndex((l) => l.value === value);
    if (idx >= 0) setActiveIndex(idx);
    else setActiveIndex(0);
  }, [value, visibleLangs]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const onKeyDown = (e) => {
    if (visibleLangs.length <= 1) return;

    if (
      !open &&
      (e.key === "ArrowDown" ||
        e.key === "ArrowUp" ||
        e.key === "Enter" ||
        e.key === " ")
    ) {
      e.preventDefault();
      setOpen(true);
      return;
    }

    if (!open) return;

    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      btnRef.current?.focus();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();

      const nextIndex = (activeIndex + 1) % visibleLangs.length;
      setActiveIndex(nextIndex);

      listRef.current
        ?.querySelectorAll("[role=option]")
        ?.[nextIndex]
        ?.focus();

      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();

      const nextIndex =
        (activeIndex - 1 + visibleLangs.length) % visibleLangs.length;

      setActiveIndex(nextIndex);

      listRef.current
        ?.querySelectorAll("[role=option]")
        ?.[nextIndex]
        ?.focus();

      return;
    }

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();

      const lang = visibleLangs[activeIndex];

      if (lang) {
        onChange?.(lang.value);
        setOpen(false);
        btnRef.current?.focus();
      }
    }
  };

  const pick = (lang, idx) => {
    onChange?.(lang.value);
    setActiveIndex(idx);
    setOpen(false);
    btnRef.current?.focus();
  };

  const styles = {
    button: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: "rgba(255,255,255,0.15)",
      color: "#fff",
      borderRadius: 12,
      padding: "3px 5px",
      cursor: visibleLangs.length > 1 ? "pointer" : "default",
      outline: "none",
      transition:
        "transform .08s ease, box-shadow .2s ease, border-color .2s ease",
      opacity: visibleLangs.length > 1 ? 1 : 0.85,
    },

    buttonHover: {
      borderColor: "rgba(255,255,255,0.25)",
      boxShadow:
        "0 8px 20px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.12)",
    },

    chevron: {
      width: 15,
      height: 15,
      display: "inline-block",
      transform: open ? "rotate(180deg)" : "none",
      transition: "transform .15s ease",
      opacity: visibleLangs.length > 1 ? 0.7 : 0.25,
    },

    flagImg: {
      width: 18,
      height: 12,
      objectFit: "cover",
      borderRadius: 2,
      boxShadow: "0 0 0 1px rgba(255,255,255,0.15) inset",
    },

    pop: {
      position: "absolute",
      right: 0,
      marginTop: 6,
      background: "rgba(18,18,18,0.85)",
      backdropFilter: "blur(8px) saturate(115%)",
      WebkitBackdropFilter: "blur(8px) saturate(115%)",
      border: "1px solid rgba(255,255,255,0.15)",
      borderRadius: 12,
      padding: 6,
      minWidth: 160,
      boxShadow: "0 10px 28px rgba(0,0,0,0.45)",
      zIndex: 14,
      maxHeight: 350,
      overflowY: "auto",
    },

    option: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      width: "100%",
      border: "none",
      background: "transparent",
      color: "#fff",
      textAlign: "left",
      padding: "8px 10px",
      borderRadius: 10,
      cursor: "pointer",
      outline: "none",
      fontSize: 14,
    },

    optionHover: {
      background: "rgba(255,255,255,0.06)",
    },

    dot: (active) => ({
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: active ? "#8fd17c" : "rgba(255,255,255,0.25)",
    }),

    wrap: {
      position: "relative",
      display: "inline-block",
    },
  };

  const [btnHover, setBtnHover] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(-1);

  return (
    <div ref={rootRef} style={styles.wrap}>
      <div
        ref={btnRef}
        className="admin__lang-wrapper"
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (visibleLangs.length <= 1) return;
          setOpen((s) => !s);
        }}
        onKeyDown={onKeyDown}
        onMouseEnter={() => setBtnHover(true)}
        onMouseLeave={() => setBtnHover(false)}
        style={{
          ...styles.button,
          ...(btnHover && visibleLangs.length > 1 ? styles.buttonHover : null),
        }}
      >
        <FlagIcon lang={selected} style={styles.flagImg} />
        <span className="text text__w">{selected.label}</span>
        <span className="chevron" style={styles.chevron}>
          ▾
        </span>
      </div>

      {open && visibleLangs.length > 1 && (
        <div
          role="listbox"
          ref={listRef}
          aria-label="Language"
          style={styles.pop}
        >
          {visibleLangs.map((lang, idx) => {
            const active = value === lang.value;
            const hovered = hoverIdx === idx;

            return (
              <button
                key={lang.value}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => pick(lang, idx)}
                onMouseEnter={() => setHoverIdx(idx)}
                onMouseLeave={() => setHoverIdx(-1)}
                style={{
                  ...styles.option,
                  ...(hovered ? styles.optionHover : null),
                }}
              >
                <FlagIcon lang={lang} style={styles.flagImg} />
                <span style={{ flex: 1 }}>{lang.fullLabel}</span>
                <span aria-hidden="true" style={styles.dot(active)} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}